import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { getMessage, listMessages, markMessageRead } from '../api/admin';
import { useAsync } from '../api/useAsync';
import { useInfiniteList } from '../api/useInfiniteList';
import { AsyncBoundary, Modal, Pill } from '../components/ui';
import type { ContactMessageRow } from '../types/admin';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

const SOURCE_LABEL: Record<string, string> = {
  site: 'Site',
  game: 'Jogo',
};

/**
 * Contact-form messages ("Falar com os desenvolvedores"), 50 per page with
 * infinite scroll. The list shows only name + subject; clicking a row opens the
 * full message and marks it read.
 */
export function MessagesPage(): React.JSX.Element {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Read-state overrides applied on top of the fetched rows, so opening (or
  // toggling) a message updates its row without refetching the whole list and
  // losing scroll position.
  const [readOverrides, setReadOverrides] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [query]);

  const fetchPage = useCallback(
    (cursor: string | null) => listMessages(cursor, debounced, PAGE_SIZE),
    [debounced],
  );
  const { items, loading, error, hasMore, sentinelRef } = useInfiniteList(fetchPage);

  const isRead = (row: ContactMessageRow): boolean => {
    const override = readOverrides[row.id];
    return override === undefined ? row.read : override;
  };

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <h1>Mensagens</h1>
          <p className="page__subtitle">
            {items.length} carregada(s){hasMore ? '…' : ''} · fale com os desenvolvedores
          </p>
        </div>
        <input
          className="search"
          placeholder="Buscar por nome ou assunto…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </header>

      {error ? <div className="banner banner--error">{error.message}</div> : null}

      <div className="data-table" role="table">
        <div className="data-table__head data-table__head--messages" role="row">
          <span role="columnheader">Nome</span>
          <span role="columnheader">Assunto</span>
        </div>

        {items.map((msg) => {
          const read = isRead(msg);
          return (
            <div
              className={`data-table__row data-table__row--messages data-table__row--clickable${
                read ? '' : ' data-table__row--unread'
              }`}
              role="row"
              key={msg.id}
              onClick={() => setSelectedId(msg.id)}
            >
              <span className="cell cell--identity" role="cell">
                {read ? null : <span className="unread-dot" aria-label="não lida" />}
                <span className="cell__name">{msg.name}</span>
              </span>
              <span className="cell" role="cell" title={msg.subject}>
                {msg.subject}
              </span>
            </div>
          );
        })}

        {!loading && items.length === 0 ? (
          <div className="empty-state">
            {debounced ? 'Nenhuma mensagem encontrada.' : 'Nenhuma mensagem ainda.'}
          </div>
        ) : null}
      </div>

      {loading ? <div className="page-loading">Carregando…</div> : null}
      {hasMore ? <div ref={sentinelRef} className="scroll-sentinel" /> : null}
      {!hasMore && items.length > 0 ? <div className="list-end">Fim da lista.</div> : null}

      {selectedId ? (
        <MessageDetailModal
          id={selectedId}
          onClose={() => setSelectedId(null)}
          onReadChange={(id, read) =>
            setReadOverrides((prev) => ({ ...prev, [id]: read }))
          }
        />
      ) : null}
    </div>
  );
}

function MessageDetailModal({
  id,
  onClose,
  onReadChange,
}: {
  id: string;
  onClose: () => void;
  onReadChange: (id: string, read: boolean) => void;
}): React.JSX.Element {
  const load = useCallback(() => getMessage(id), [id]);
  const { data, loading, error, reload } = useAsync(load, [id]);
  const [read, setRead] = useState<boolean | null>(null);
  const [toggling, setToggling] = useState(false);

  // Auto-mark read when the message is opened. Only writes when it isn't already
  // read, so re-opening a read message is a no-op.
  useEffect(() => {
    if (!data) return;
    setRead(data.read);
    if (!data.read) {
      markMessageRead(id, true)
        .then(() => {
          setRead(true);
          onReadChange(id, true);
        })
        .catch(() => {
          /* leave it unread; the list still reflects the server state */
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, id]);

  const toggleRead = async (): Promise<void> => {
    if (read === null) return;
    const next = !read;
    setToggling(true);
    try {
      await markMessageRead(id, next);
      setRead(next);
      onReadChange(id, next);
    } catch {
      /* keep current state on failure */
    } finally {
      setToggling(false);
    }
  };

  return (
    <Modal title={data ? data.subject : 'Mensagem'} onClose={onClose} wide>
      <AsyncBoundary loading={loading} error={error} onRetry={reload}>
        {data ? (
          <div className="message-detail">
            <dl className="message-detail__meta">
              <dt>Nome</dt>
              <dd>{data.name}</dd>

              <dt>E-mail</dt>
              <dd>
                {data.email ? (
                  <a href={`mailto:${data.email}`}>{data.email}</a>
                ) : (
                  <span className="cell--dim">não informado</span>
                )}
              </dd>

              <dt>Origem</dt>
              <dd>
                <Pill>{SOURCE_LABEL[data.source] ?? data.source}</Pill>
              </dd>

              <dt>Recebida em</dt>
              <dd>{new Date(data.createdAt).toLocaleString('pt-BR')}</dd>
            </dl>

            <div className="message-detail__body">{data.message}</div>

            <div className="modal__actions">
              <button
                type="button"
                className="btn"
                onClick={() => void toggleRead()}
                disabled={toggling || read === null}
              >
                {read ? 'Marcar como não lida' : 'Marcar como lida'}
              </button>
              <button type="button" className="btn btn--primary" onClick={onClose}>
                Fechar
              </button>
            </div>
          </div>
        ) : null}
      </AsyncBoundary>
    </Modal>
  );
}
